/**
 * SSE Progress Hook
 * 
 * React hook for streaming workflow progress via Server-Sent Events.
 * Supports document extraction, comparison matching, and supplement generation.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { auth } from '@/lib/firebase';
import { type ReasoningLogEntry } from '@/lib/reasoning-log-utils';
import { env } from '@/config/env';

/**
 * SSE event types
 */
export type SSEEventType = 'snapshot' | 'progress' | 'stage_change' | 'completed' | 'failed' | 'heartbeat' | 'error' | 'reasoning';

/**
 * SSE progress data structure
 */
export interface SSEProgressData {
  workflowId: string;
  stage: string;
  progress: number;
  message: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
  error?: string;
}

/**
 * SSE event structure
 */
export interface SSEEvent {
  type: SSEEventType;
  data: SSEProgressData;
}

/**
 * Workflow types supported by SSE
 */
export type SSEWorkflowType = 'extraction' | 'matching' | 'supplement';

/**
 * SSE connection state
 */
export type SSEConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error';

/**
 * Hook return type
 */
export interface UseSSEProgressResult {
  /** Current progress data */
  progress: SSEProgressData | null;
  /** Whether the workflow is complete */
  isComplete: boolean;
  /** Whether the workflow failed */
  isFailed: boolean;
  /** Current connection state */
  connectionState: SSEConnectionState;
  /** Error message if any */
  error: string | null;
  /** Function to manually disconnect */
  disconnect: () => void;
  /** Function to manually reconnect */
  reconnect: () => void;
  /** Normalized reasoning log entries */
  reasoningLog: ReasoningLogEntry[];
}

/**
 * Build SSE URL for a workflow
 */
function buildSSEUrl(workflowType: SSEWorkflowType, resourceId: number): string {
  const paths: Record<SSEWorkflowType, string> = {
    extraction: `/api/stream/extraction/${resourceId}`,
    matching: `/api/stream/comparison/${resourceId}`,
    supplement: `/api/stream/supplement/${resourceId}`,
  };
  return `${env.apiBaseUrl}${paths[workflowType]}`;
}

/**
 * Hook to stream workflow progress via SSE
 * 
 * @param workflowType - Type of workflow to stream
 * @param resourceId - ID of the resource (document, comparison, or supplement)
 * @param enabled - Whether to enable the SSE connection
 * @returns SSE progress state and controls
 * 
 * @example
 * ```tsx
 * // Stream document extraction progress
 * const { progress, isComplete, error } = useSSEProgress('extraction', documentId);
 * 
 * // Stream supplement generation with reasoning log
 * const { progress, reasoningLog } = useSSEProgress('supplement', supplementId);
 * ```
 */
export function useSSEProgress(
  workflowType: SSEWorkflowType,
  resourceId: number | null,
  enabled: boolean = true
): UseSSEProgressResult {
  const [progress, setProgress] = useState<SSEProgressData | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [connectionState, setConnectionState] = useState<SSEConnectionState>('disconnected');
  const [error, setError] = useState<string | null>(null);
  const [reasoningLog, setReasoningLog] = useState<ReasoningLogEntry[]>([]);

  const abortControllerRef = useRef<AbortController | null>(null);
  const reconnectAttemptRef = useRef(0);
  const maxReconnectAttempts = 3;

  const disconnect = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setConnectionState('disconnected');
  }, []);

  const connect = useCallback(async () => {
    if (!resourceId || !enabled) {
      console.log('[useSSEProgress] Not connecting:', { resourceId, enabled, workflowType });
      return;
    }

    console.log('[useSSEProgress] Initiating connection:', { workflowType, resourceId, enabled });

    // Abort any existing connection
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setConnectionState('connecting');
    setError(null);

    try {
      // Get Firebase auth token
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Not authenticated');
      }
      
      // Force token refresh to get a fresh token for SSE connection
      const token = await user.getIdToken(true);

      const url = buildSSEUrl(workflowType, resourceId);
      console.log('[useSSEProgress] Connecting to:', url);

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache',
        },
        signal: abortController.signal,
      });

      if (!response.ok) {
        console.error('[useSSEProgress] HTTP error:', response.status, response.statusText);
        
        // If 401, try to reconnect with fresh token
        if (response.status === 401) {
          console.log('[useSSEProgress] 401 error, will retry with fresh token');
          throw new Error('Authentication expired, reconnecting...');
        }
        
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      console.log('[useSSEProgress] Connected successfully');
      setConnectionState('connected');
      reconnectAttemptRef.current = 0;

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('No response body');
      }

      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');

        // Keep the last incomplete line in the buffer
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event: SSEEvent = JSON.parse(line.slice(6));

              // Skip heartbeat events for progress updates
              if (event.type === 'heartbeat') {
                continue;
              }

              // Handle snapshot events (initial state on connection)
              if (event.type === 'snapshot') {
                const metadata = event.data.metadata || {};
                setProgress(event.data);

                // Load historical reasoning logs from snapshot
                if (metadata.reasoningLogs && Array.isArray(metadata.reasoningLogs)) {
                  const logs = metadata.reasoningLogs as Array<{
                    id?: number;
                    activityName?: string;
                    reasoningContent?: string;
                    tokenCount?: number;
                    createdAt?: string;
                  }>;
                  const normalizedLogs: ReasoningLogEntry[] = logs
                    .filter(log => log && typeof log.reasoningContent === 'string')
                    .map((log, index) => ({
                      id: log.id ?? index,
                      activityName: log.activityName ?? 'reasoning',
                      reasoningContent: log.reasoningContent!,
                      tokenCount: log.tokenCount,
                      createdAt: log.createdAt ?? new Date().toISOString()
                    }));
                  setReasoningLog(normalizedLogs);
                }

                // Check if workflow is already complete/failed
                if (metadata.status === 'completed') {
                  setIsComplete(true);
                  // Don't disconnect - may still receive updates
                }
                if (metadata.status === 'failed') {
                  setIsFailed(true);
                  setError(event.data.error || 'Workflow failed');
                }
                continue;
              }

              // Only update main progress state for non-reasoning events
              // Reasoning events should not override the main status/message
              if (event.type !== 'reasoning') {
                setProgress(event.data);
              }

              // Handle reasoning events (for AI reasoning log streaming)
              if (event.type === 'reasoning') {
                const newEntry: ReasoningLogEntry = {
                  id: Date.now(),
                  // Use specific activity name from metadata if available, otherwise stage, otherwise default
                  activityName: (event.data.metadata?.activityName as string) || event.data.stage || 'reasoning',
                  reasoningContent: event.data.message,
                  createdAt: event.data.timestamp || new Date().toISOString()
                };
                setReasoningLog(prev => [...prev, newEntry]);
                continue;
              }

              // Handle stage_change events
              if (event.type === 'stage_change') {
                // Progress data is already set above
                continue;
              }

              // Extract reasoning chunks for supplement generation from metadata
              if (workflowType === 'supplement' && event.data.metadata?.reasoningChunks) {
                const chunks = event.data.metadata.reasoningChunks as string[];
                const normalizedChunks: ReasoningLogEntry[] = chunks.map((content, index) => ({
                  id: index,
                  activityName: 'reasoning',
                  reasoningContent: content,
                  createdAt: new Date().toISOString()
                }));
                setReasoningLog(normalizedChunks);
              }

              if (event.type === 'completed') {
                setIsComplete(true);
                disconnect();
                return;
              }

              if (event.type === 'failed' || event.type === 'error') {
                setIsFailed(true);
                setError(event.data.error || 'Workflow failed');
                disconnect();
                return;
              }
            } catch {
              // Ignore parse errors for malformed events
              console.warn('Failed to parse SSE event:', line);
            }
          }
        }
      }
    } catch (err) {
      if (err instanceof Error) {
        if (err.name === 'AbortError') {
          // Connection was intentionally aborted
          return;
        }

        setError(err.message);
        setConnectionState('error');

        // Attempt reconnection
        if (reconnectAttemptRef.current < maxReconnectAttempts) {
          reconnectAttemptRef.current++;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptRef.current), 10000);
          setTimeout(() => {
            if (enabled && resourceId) {
              connect();
            }
          }, delay);
        }
      }
    }
  }, [workflowType, resourceId, enabled, disconnect]);

  const reconnect = useCallback(() => {
    reconnectAttemptRef.current = 0;
    setIsComplete(false);
    setIsFailed(false);
    setError(null);
    setProgress(null);
    setReasoningLog([]);
    connect();
  }, [connect]);

  // Connect when enabled and resourceId is available
  useEffect(() => {
    if (enabled && resourceId && !isComplete && !isFailed) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, resourceId, connect, disconnect, isComplete, isFailed]);

  return {
    progress,
    isComplete,
    isFailed,
    connectionState,
    error,
    disconnect,
    reconnect,
    reasoningLog,
  };
}

/**
 * Hook specifically for document extraction progress
 */
export function useDocumentExtractionSSE(documentId: number | null, enabled: boolean = true) {
  return useSSEProgress('extraction', documentId, enabled);
}

/**
 * Hook specifically for comparison matching progress
 */
export function useComparisonMatchingSSE(comparisonId: number | null, enabled: boolean = true) {
  return useSSEProgress('matching', comparisonId, enabled);
}

/**
 * Hook specifically for supplement generation progress with reasoning log
 */
export function useSupplementGenerationSSE(supplementId: number | null, enabled: boolean = true) {
  const result = useSSEProgress('supplement', supplementId, enabled);

  return {
    ...result,
    /** Reasoning log chunks streamed from AI */
    reasoningLog: result.reasoningLog,
  };
}

export default useSSEProgress;
